<template>
  <v-dialog v-bind:value="show" @click:outside="$emit('close')" max-width="1000" scrollable>
    <v-card>
      <v-card-title class="headline">
        <v-icon left color="info">help_outline</v-icon>
        Guía de Conciliación bancaria
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="max-height: 75vh" class="body-2">
        <p class="mt-4">
          La conciliación compara los movimientos de una cuenta bancaria registrados en Syscore con el extracto que informa el banco,
          para un período. El objetivo es que, al cierre, la diferencia entre ambos saldos quede <b>explicada</b> por partidas
          conciliatorias (movimientos que el banco todavía no procesó).
        </p>

        <p class="subtitle-1 font-weight-bold mt-4 mb-2">1. Flujo</p>
        <ol>
          <li><b>Nueva conciliación:</b> elegí la cuenta (solo tipo Banco), el período y subí el Excel de movimientos del banco.</li>
          <li><b>Premacheo:</b> al procesar, los movimientos con el mismo importe y la misma fecha (o el mismo nº de cheque) se concilian solos.</li>
          <li><b>A conciliar:</b> revisá las sugerencias y conciliá a mano lo que falte, registrá lo que solo está en el banco y marcá como partida lo que solo está en Syscore.</li>
          <li><b>Guardar conciliación:</b> cuando no queden pendientes y los saldos ajustados coincidan, se cierra.</li>
        </ol>
        <p>
          Cada acción se guarda en el momento: podés salir y retomar el borrador cuando quieras. "Guardar borrador" solo registra la
          fecha de guardado.
        </p>

        <v-divider class="my-4"></v-divider>

        <p class="subtitle-1 font-weight-bold mb-2">2. Formatos de extracto</p>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Formato</th>
              <th>Cómo se obtiene</th>
              <th>Particularidades</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Banco Galicia</td>
              <td>Office Banking → Movimientos → Exportar Excel</td>
              <td>Débitos y créditos en columnas separadas. Los movimientos en estado <i>Pendiente</i> no se importan.</td>
            </tr>
            <tr>
              <td>Banco Provincia</td>
              <td>BIP → Detalle de movimientos → Excel</td>
              <td>Viene ordenado del más nuevo al más viejo; se reordena por número de secuencia. Se lee el nº de cheque y el CUIT de las transferencias.</td>
            </tr>
            <tr>
              <td>ICBC</td>
              <td>Office Banking → Movimientos de cuenta → Excel</td>
              <td>Viene del más nuevo al más viejo. Débitos y créditos en columnas separadas. Se lee el nº de cheque y el CUIT de los débitos inmediatos.</td>
            </tr>
            <tr>
              <td>Banco Santander</td>
              <td>Online Banking Empresas → Últimos movimientos → Descargar</td>
              <td>
                Trae los "Movimientos del Día" (sin saldo) y los "Últimos Movimientos" (con saldo). El saldo se controla en las filas que lo
                informan, y se avisa si el "Saldo al" del banco no coincide con el calculado.
              </td>
            </tr>
            <tr>
              <td>Genérico</td>
              <td>Cualquier Excel/CSV</td>
              <td>En Tesorería → Cuentas se indica en qué columna (A, B, C...) está la fecha, la descripción, el importe (o débito/crédito) y el saldo.</td>
            </tr>
          </tbody>
        </v-simple-table>
        <p class="mt-2">
          El formato se toma de la cuenta (Tesorería → Cuentas → Formato de extracto). Si no se elige, se usa el del banco de la cuenta
          cuando está soportado.
        </p>

        <v-divider class="my-4"></v-divider>

        <p class="subtitle-1 font-weight-bold mb-2">3. Validaciones al procesar el archivo</p>
        <ul>
          <li><b>Formato:</b> las columnas deben coincidir con el banco de la cuenta; si el archivo es de otro banco se avisa cuál.</li>
          <li><b>Saldos:</b> se verifica fila por fila que saldo anterior + importe = saldo informado. Un archivo editado o incompleto se rechaza.</li>
          <li><b>Archivo ya conciliado:</b> si el mismo archivo pertenece a una conciliación cerrada, no se puede volver a usar.</li>
          <li><b>Período:</b> no puede superponerse con una conciliación cerrada. Los movimientos del archivo fuera del período no se importan.</li>
          <li><b>Continuidad:</b> se avisa si el período no empieza al día siguiente de la conciliación anterior o si el saldo inicial no coincide con su saldo final.</li>
          <li><b>Borrador:</b> cada cuenta puede tener un único borrador abierto; se puede retomar o descartar.</li>
        </ul>

        <v-divider class="my-4"></v-divider>

        <p class="subtitle-1 font-weight-bold mb-2">4. Indicadores y resumen</p>
        <ul>
          <li><b>Saldo Syscore / Saldo banco:</b> saldos al último día del período, con el saldo inicial debajo.</li>
          <li><b>Diferencia de saldos:</b> Banco − Syscore antes de ajustar.</li>
          <li><b>Avance:</b> grupos conciliados sobre el total de grupos, pendientes y partidas.</li>
          <li>
            <b>Resumen (solapa Partidas):</b> al saldo del extracto se le suman las partidas de Syscore que el banco todavía no procesó
            (<i>saldo bancario ajustado</i>); al saldo de Syscore se le suman los movimientos del banco aún no registrados
            (<i>saldo Syscore ajustado</i>). Si ambos coinciden, la conciliación cierra.
          </li>
        </ul>

        <v-divider class="my-4"></v-divider>

        <p class="subtitle-1 font-weight-bold mb-2">5. Conciliar</p>
        <ul>
          <li><b>Sugerencias:</b> mismo importe con fecha cercana (±3 días) o mismo CUIT. Se resaltan en amarillo; aceptalas todas o de a una.</li>
          <li>
            <b>Manual (N ↔ M):</b> tildá movimientos de Syscore a la izquierda y del banco a la derecha. Si los totales son iguales se habilita
            "Conciliar selección". Ejemplo: un depósito de 3 cheques en Syscore contra 3 acreditaciones en el banco.
          </li>
          <li><b>Compensados:</b> un movimiento y su reversión en Syscore se pueden conciliar entre sí, sin línea del banco.</li>
          <li><b>Desconciliar:</b> en la solapa Conciliados, devuelve los movimientos a "A conciliar".</li>
        </ul>

        <v-divider class="my-4"></v-divider>

        <p class="subtitle-1 font-weight-bold mb-2">6. Movimientos que están en un solo lado</p>
        <ul>
          <li>
            <b>Solo en el banco</b> (comisiones, impuestos, intereses, transferencias no cargadas): menú ⋮ → "Crear Mov. de tesorería".
            Se abre el movimiento de tesorería con la cuenta bancaria, la fecha y el importe ya cargados (se puede precargar una
            plantilla); al guardarlo queda conciliado. Si seleccionás varias líneas del mismo signo se registran en un solo movimiento, y el sistema pregunta cómo cargarlas: <b>sumado en una línea</b> (una línea de banco por el total y una contrapartida) o <b>línea por línea</b> (una línea de banco por el total y una línea por cada movimiento, con su importe y el texto del banco como motivo). Si elegís una plantilla, cada movimiento toma la cuenta y el concepto de la línea que coincida con su código o texto. En los dos casos el movimiento lleva la fecha más reciente.
          </li>
          <li>
            <b>Reglas de conciliación</b> (Tesorería → Reglas de conciliación): asocian un texto del extracto (ej. <code>907176</code> o
            <code>IMPUESTO DEBITO - LEY 25413</code>) a una plantilla. Las líneas que cumplen una regla se registran todas juntas con
            "Registrar por reglas".
          </li>
          <li>
            <b>Solo en Syscore</b> (cheque emitido no debitado, depósito no acreditado): menú ⋮ → "Marcar como partida conciliatoria".
            Quedan en la solapa Partidas y aparecen en la próxima conciliación de la cuenta para conciliarlas cuando el banco las procese.
          </li>
        </ul>

        <v-divider class="my-4"></v-divider>

        <p class="subtitle-1 font-weight-bold mb-2">7. Cierre y reapertura</p>
        <ul>
          <li>Para guardar la conciliación no pueden quedar movimientos del banco sin registrar ni de Syscore sin conciliar o marcar como partida, y los saldos ajustados deben coincidir.</li>
          <li>Un movimiento de tesorería conciliado en una conciliación cerrada no se puede revertir.</li>
          <li>Solo se puede reabrir la última conciliación de la cuenta (requiere permiso de datos sensibles).</li>
          <li>Desde el ícono de impresora se descarga el resumen en PDF; cada solapa se puede exportar a Excel.</li>
        </ul>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('close')">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    show: Boolean,
  },
};
</script>
